import { useAuthStoreShallow } from "@/features/authentication";
import Tooltip from "@components/ui/tooltip";
import { SESSIONS_TABLE_SIZES } from "@pages/analytics/constants";
import type { ReaderType } from "@pages/analytics/types";
import { ReaderMetaInfo } from "@pages/analytics/components/reader-meta-info";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import {
  IconCheck,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDeviceTablet,
  IconRobot,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import moment from "moment";
import { useCallback } from "react";
import { AreYouSureDialog } from "../are-you-sure-dialog";
import { DeleteVersionLockDialog } from "../delete-version-lock-dialog";
import { SessionDetails } from "../session-details";
import { SessionRowActions } from "./session-row-actions";

const DEVICE_ICONS = {
  Desktop: IconDeviceDesktop,
  Mobile: IconDeviceMobile,
  Tablet: IconDeviceTablet,
} as const;

// Only non-human traffic gets a mark. A column reading "Human" on 95% of rows
// costs width and draws the eye nowhere; a badge on the handful of rows that
// are not a person is what someone scanning this table is looking for.
const TRAFFIC_ICONS = {
  AI: IconSparkles,
  "Other bots": IconRobot,
} as const;

export const SessionsTableRow = ({
  session,
  pageTitle,
  versionTitle,
  storyId,
  versionId,
  allSessions,
  isOwner,
}: {
  session: ReaderType;
  pageTitle: string;
  versionTitle: string;
  storyId: string;
  versionId: string;
  allSessions?: ReaderType[];
  isOwner: boolean;
}) => {
  const DeviceIcon = DEVICE_ICONS[session.device as keyof typeof DEVICE_ICONS];
  const TrafficIcon =
    TRAFFIC_ICONS[session.traffic_type as keyof typeof TRAFFIC_ICONS];
  const dispatch = useAppDispatch();
  const { isUserOrCreatorLite } = useAuthStoreShallow((s) => {
    return {
      isUserOrCreatorLite: s.isUserOrCreatorLite,
    };
  });

  const progressValue = (session.last_slide / session.slides_count) * 100;
  const progress = Number.isInteger(progressValue) ? progressValue : Number(progressValue.toFixed(1));

  const handleOpenSessionDetails = useCallback(() => {
    dispatch(
      openDialog({
        id: SessionDetails.id,
        content: SessionDetails,
        props: {
          session,
          pageTitle,
          versionTitle,
          storyId,
          versionId,
          allSessions,
          className: "tw-w-full",
        },
      }),
    );
  }, [
    dispatch,
    session,
    pageTitle,
    versionTitle,
    storyId,
    versionId,
    allSessions,
  ]);

  const handleDeleteSession = useCallback(async () => {
    if (isUserOrCreatorLite && !isOwner) {
      dispatch(
        openDialog({
          id: DeleteVersionLockDialog.id,
          content: DeleteVersionLockDialog,
          props: {
            type: "session",
          },
        }),
      );
      return;
    }
    dispatch(
      openDialog({
        id: AreYouSureDialog.id,
        content: AreYouSureDialog,
        props: {
          sessionId: session.session_id,
        },
        zIndex: 10001,
      }),
    );
  }, [dispatch, isUserOrCreatorLite, isOwner, session.session_id]);

  return (
    <div className="tw-flex tw-items-center tw-bg-white hover:tw-bg-blue-25 tw-border-b tw-border-solid tw-border-blue-100 tw-py-0.5 tw-min-h-[53px] tw-transition-colors last:tw-border-b-0 group/session-row">
      {/* Reader column */}
      <div className="tw-flex-auto tw-px-2 tw-pl-4 tw-py-2 tw-flex tw-items-center tw-min-w-0 tw-w-80 tw-shrink-0 tw-mr-auto -2xl:tw-w-52 -md:tw-w-60">
        <div className="tw-flex tw-flex-col tw-gap-1 tw-justify-center tw-min-w-0 tw-w-full">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
            <p
              role="button"
              tabIndex={0}
              className="tw-font-sans tw-font-medium tw-text-sm tw-text-blue tw-truncate tw-cursor-pointer"
              onClick={handleOpenSessionDetails}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpenSessionDetails(); }}
            >
              {session.reader_name || `Reader #${session.visitor_index}`}
            </p>
            {!session.single_session_visitor && (
              <div className="tw-bg-blue-100 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <p className="tw-font-sans tw-font-normal tw-text-xs tw-text-blue-500 tw-leading-4 tw-whitespace-nowrap">
                  Session #{session.session_index}
                </p>
              </div>
            )}
          </div>
          <ReaderMetaInfo
            position={session.reader_position}
            email={session.reader_email}
            customFields={session.reader_custom_fields}
          />
        </div>
      </div>

      {/* Session started column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: SESSIONS_TABLE_SIZES.SESSION_STARTED }}
      >
        <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500 tw-truncate">
          {moment.utc(session.timestamp).local().format("D MMM · h:mm A")}
        </p>
      </div>

      {/* Reading time column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: SESSIONS_TABLE_SIZES.READING_TIME }}
      >
        <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500 tw-truncate">
          {session.duration}
        </p>
      </div>

      {/* Clicked CTA column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: SESSIONS_TABLE_SIZES.CLICKED_CTA }}
      >
        {session.click_cta ? (
          <IconCheck className="tw-size-6 tw-text-green-500" stroke={2} />
        ) : (
          <IconX className="tw-size-6 tw-text-blue-500" stroke={2} />
        )}
      </div>

      {/* Progress column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: SESSIONS_TABLE_SIZES.PROGRESS }}
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-size-6 tw-flex tw-items-center tw-justify-center">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className="tw-transform -tw-rotate-90"
              aria-label={`${progress}% progress`}
            >
              <title>{progress}% progress</title>
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="#E7E6EC"
                strokeWidth="3"
              />
              {progress && (
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#610CFF"
                  strokeWidth="3"
                  strokeDasharray={`${progress * 0.628} 62.8`}
                  strokeLinecap="round"
                />
              )}
            </svg>
          </div>
          <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500 tw-whitespace-nowrap">
            {progress}%
          </p>
        </div>
      </div>

      {/* Location column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: SESSIONS_TABLE_SIZES.LOCATION }}
      >
        <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500 tw-truncate">
          {session.city ? `${session.city}, ` : ""}
          {session.country}
        </p>
      </div>

      {/* Device column with menu */}
      <div
        className="tw-px-2 tw-pr-4 tw-flex tw-items-center tw-justify-between tw-h-[53px]"
        style={{ width: SESSIONS_TABLE_SIZES.DEVICE }}
      >
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <Tooltip>
            <Tooltip.Trigger>
              <DeviceIcon className="tw-size-3.5 tw-text-blue-500 tw-flex-shrink-0" />
            </Tooltip.Trigger>
            <Tooltip.Content className="tw-z-[10000]">
              <Tooltip.Text>{session.device}</Tooltip.Text>
            </Tooltip.Content>
          </Tooltip>
          {TrafficIcon && (
            <Tooltip>
              <Tooltip.Trigger>
                <TrafficIcon className="tw-size-3.5 tw-text-blue-500 tw-flex-shrink-0" />
              </Tooltip.Trigger>
              <Tooltip.Content className="tw-z-[10000]">
                <Tooltip.Text>
                  {session.bot_signature
                    ? `${session.traffic_type} · ${session.bot_signature}`
                    : session.traffic_type}
                </Tooltip.Text>
              </Tooltip.Content>
            </Tooltip>
          )}
        </div>
        <SessionRowActions
          onSessionDetails={handleOpenSessionDetails}
          onDeleteSession={handleDeleteSession}
        />
      </div>
    </div>
  );
};
